import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import TopNavbar from './TopNavbar';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalProject } from '../../contexts/GlobalProjectContext';

/** When orgId from auth changes, clear selected project and refresh list. */
function AuthOrgSync() {
  const { user } = useAuth();
  const { setSelectedProject, refreshProjects } = useGlobalProject();
  const prevOrgId = useRef<string | null>(null);
  useEffect(() => {
    const orgId = user?.orgId ?? null;
    if (prevOrgId.current !== null && prevOrgId.current !== orgId) {
      setSelectedProject(null);
      refreshProjects();
    }
    prevOrgId.current = orgId;
  }, [user?.orgId, setSelectedProject, refreshProjects]);
  return null;
}

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  title,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen w-full max-w-full overflow-hidden bg-background text-foreground">
      <AuthOrgSync />
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden" style={{ maxWidth: '100%' }}>
        <a
          href="#main-content"
          className="sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:m-0 focus:inline-flex focus:h-auto focus:w-auto focus:overflow-visible focus:whitespace-normal focus:rounded-md focus:border focus:border-border focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:text-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        <TopNavbar
          title={title}
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
        />
        <main
          id="main-content"
          className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto bg-background"
          style={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;